import { useState, useEffect } from 'react';

interface ChartDimensions {
    width: number;
    height: number;
    legendPosition: 'top' | 'right' | 'bottom';
    maxTicksLimit: number;
    tickRotation: number;
}

function calculateDimensions(vw: number, vh: number): ChartDimensions {
    if (vw < 1400) {
        return {
            width: vw,
            height: Math.min(350, vh * 0.5),
            legendPosition: vh < 700 ? 'top' : 'bottom',
            maxTicksLimit: 6,
            tickRotation: 45,
        };
    }

    if (vw < 1800) {
        return {
            width: vw,
            height: Math.min(450, vh * 0.55),
            legendPosition: 'bottom',
            maxTicksLimit: 10,
            tickRotation: 30,
        };
    }

    if (vw < 2200) {
        return {
            width: vw,
            height: Math.min(550, vh * 0.6),
            legendPosition: 'right',
            maxTicksLimit: 15,
            tickRotation: 0,
        };
    }

    return {
        width: vw,
        height: Math.min(700, vh * 0.65),
        legendPosition: 'right',
        maxTicksLimit: 20,
        tickRotation: 0,
    };
}

export const useChartDimensions = (): ChartDimensions => {
    const [dimensions, setDimensions] = useState<ChartDimensions>(() =>
        calculateDimensions(window.innerWidth, window.innerHeight)
    );

    useEffect(() => {
        const handleResize = () => {
            setDimensions(
                calculateDimensions(window.innerWidth, window.innerHeight)
            );
        };

        let timeoutId: ReturnType<typeof setTimeout>;
        const debouncedResize = () => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(handleResize, 150);
        };

        window.addEventListener('resize', debouncedResize);
        return () => {
            clearTimeout(timeoutId);
            window.removeEventListener('resize', debouncedResize);
        };
    }, []);

    return dimensions;
};

export default useChartDimensions;
